import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Search,
  Plus,
  RefreshCw,
  FileText,
  Printer,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  listIssueSlips,
  getIssueSlip,
  type IssueSlipListItem,
  type IssueSlipDetail,
} from '@/services/warehouse/issueSlipsApi'
import { CreateIssueSlipModal } from './CreateIssueSlipModal'
import { IssueSlipDetailModal } from './IssueSlipDetailModal'
import { IssueSlipPrintView } from './IssueSlipPrintView'

/** Bỏ dấu + chữ thường để tìm tên vật tư không cần gõ dấu. */
function foldText(value: string): string {
  return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().trim()
}

function formatDateTimeVi(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

interface IssueSlipsTabProps {
  initialSlips?: IssueSlipListItem[]
}

export function IssueSlipsTab({ initialSlips }: IssueSlipsTabProps = {}): React.ReactElement {
  const [items, setItems] = useState<IssueSlipListItem[]>(initialSlips ?? [])
  const [loading, setLoading] = useState(initialSlips === undefined)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [materialFilter, setMaterialFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'VOID'>('ALL')

  // Modals & views
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [selectedSlipId, setSelectedSlipId] = useState<string | null>(null)
  const [printSlip, setPrintSlip] = useState<IssueSlipDetail | null>(null)

  const loadSlips = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listIssueSlips()
      setItems(data)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải danh sách phiếu xuất kho')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialSlips === undefined) {
      void loadSlips()
    }
  }, [loadSlips, initialSlips])

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter !== 'ALL' && item.status !== statusFilter) return false
      if (materialFilter.trim()) {
        const wanted = foldText(materialFilter)
        if (!(item.line_items ?? []).some((line) => foldText(line.name).includes(wanted))) return false
      }
      if (search.trim()) {
        const q = search.toLowerCase()
        const matchCode = (item.code || '').toLowerCase().includes(q)
        const matchPurpose = (item.purpose || '').toLowerCase().includes(q)
        const matchCreator = (item.created_by_name || item.created_by || '').toLowerCase().includes(q)
        const matchItems = (item.line_items ?? []).some((line) => foldText(line.name).includes(foldText(search)))
        if (!matchCode && !matchPurpose && !matchCreator && !matchItems) return false
      }
      return true
    })
  }, [items, search, statusFilter, materialFilter])

  async function handleOpenPrint(slip: IssueSlipDetail) {
    setPrintSlip(slip)
  }

  async function handleDirectPrint(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    try {
      const full = await getIssueSlip(id)
      setPrintSlip(full)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải phiếu để in')
    }
  }

  // If currently printing, show full print view
  if (printSlip) {
    return <IssueSlipPrintView slip={printSlip} onBack={() => setPrintSlip(null)} />
  }

  return (
    <div className="space-y-3 font-sans">
      {/* Top action bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative min-w-[220px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm mã phiếu, vật tư xuất, mục đích, người lập..."
              className="h-9 w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white pl-8 pr-3 text-xs outline-none focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
            />
          </div>

          {/* Lọc riêng theo vật tư xuất */}
          <input
            type="text"
            value={materialFilter}
            onChange={(e) => setMaterialFilter(e.target.value)}
            placeholder="Lọc theo vật tư xuất…"
            aria-label="Lọc theo vật tư xuất"
            className="h-9 w-[220px] rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 text-xs outline-none focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)]"
          />

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="h-9 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 text-xs outline-none focus:border-[var(--color-accent)]"
          >
            <option value="ALL">Tất cả trạng thái</option>
            <option value="ACTIVE">Hoạt động</option>
            <option value="VOID">Đã hủy</option>
          </select>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadSlips()}
            disabled={loading}
            title="Tải lại danh sách"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Create button */}
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Tạo phiếu xuất kho
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-[var(--radius-md)] border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
          {error}
        </div>
      ) : null}

      {/* Main Table */}
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white shadow-xs">
        <table className="w-full border-collapse text-left text-xs">
          <thead className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-slate-50 font-semibold uppercase tracking-wider text-slate-600">
            <tr>
              <th className="w-12 px-3 py-2.5 text-center whitespace-nowrap">STT</th>
              <th className="w-32 min-w-[130px] px-3 py-2.5 whitespace-nowrap">Mã phiếu</th>
              <th className="w-36 min-w-[140px] px-3 py-2.5 whitespace-nowrap">Ngày lập</th>
              <th className="min-w-[200px] px-3 py-2.5">Mục đích xuất</th>
              <th className="min-w-[260px] px-3 py-2.5">Vật tư xuất</th>
              <th className="w-36 min-w-[140px] px-3 py-2.5 whitespace-nowrap">Người lập</th>
              <th className="w-24 min-w-[80px] px-3 py-2.5 text-center whitespace-nowrap">Số dòng</th>
              <th className="w-28 min-w-[100px] px-3 py-2.5 text-center whitespace-nowrap">Trạng thái</th>
              <th className="w-28 min-w-[90px] px-3 py-2.5 text-center whitespace-nowrap">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-12 text-center text-slate-400">
                  <div className="inline-flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                    Đang tải danh sách phiếu xuất kho...
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-12 text-center text-slate-400">
                  <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                  {items.length === 0
                    ? 'Chưa có phiếu xuất kho nào. Nhấn "+ Tạo phiếu xuất kho" để lập phiếu mới.'
                    : 'Không tìm thấy phiếu xuất kho phù hợp.'}
                </td>
              </tr>
            ) : (
              filtered.map((item, index) => {
                const isVoid = item.status === 'VOID'
                return (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedSlipId(item.id)}
                    className="cursor-pointer transition-colors hover:bg-slate-50/90"
                    title="Bấm để xem chi tiết phiếu xuất"
                  >
                    {/* Cột 1: STT */}
                    <td className="px-3 py-2.5 text-center text-slate-400 tabular-nums whitespace-nowrap">
                      {index + 1}
                    </td>

                    {/* Cột 2: Mã phiếu */}
                    <td className="px-3 py-2.5 font-mono font-bold text-[var(--color-link)] whitespace-nowrap">
                      {item.code}
                    </td>

                    {/* Cột 3: Ngày lập */}
                    <td className="px-3 py-2.5 text-slate-600 tabular-nums whitespace-nowrap">
                      {formatDateTimeVi(item.created_at)}
                    </td>

                    {/* Cột 4: Mục đích xuất */}
                    <td className="px-3 py-2.5 font-medium text-slate-900 break-words min-w-[200px]">
                      {item.purpose}
                      {item.note ? (
                        <span className="block text-[11px] text-slate-400 italic font-normal break-words">
                          {item.note}
                        </span>
                      ) : null}
                    </td>

                    {/* Cột: Vật tư xuất trong phiếu (tìm ô tìm kiếm ra phiếu có vật tư này) */}
                    <td className="px-3 py-2.5 text-slate-700 break-words min-w-[260px]">
                      {(item.line_items ?? []).length === 0 ? '—' : (item.line_items ?? []).map((line, i) => (
                        <div key={i}>{line.name} <span className="text-slate-500">× {line.qty.toLocaleString('vi-VN')} {line.uom}</span></div>
                      ))}
                    </td>

                    {/* Cột 5: Người lập */}
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                      {item.created_by_name || item.created_by}
                    </td>

                    {/* Cột 6: Số dòng */}
                    <td className="px-3 py-2.5 text-center text-slate-600 tabular-nums whitespace-nowrap">
                      {item.line_count != null ? item.line_count : '—'}
                    </td>

                    {/* Cột 7: Trạng thái */}
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <span
                        className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${
                          !isVoid
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {!isVoid ? 'Hoạt động' : 'Đã hủy'}
                      </span>
                    </td>

                    {/* Cột 8: Thao tác */}
                    <td
                      className="px-3 py-2.5 text-center whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={(e) => void handleDirectPrint(e, item.id)}
                        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50 whitespace-nowrap"
                        title="In phiếu xuất kho (A4)"
                      >
                        <Printer className="h-3 w-3 text-slate-600 shrink-0" />
                        In
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <CreateIssueSlipModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={(created) => {
          void loadSlips()
          setSelectedSlipId(created.id)
        }}
      />

      {/* Detail Modal */}
      <IssueSlipDetailModal
        slipId={selectedSlipId}
        onClose={() => setSelectedSlipId(null)}
        onPrint={handleOpenPrint}
        onVoidSuccess={() => void loadSlips()}
      />
    </div>
  )
}
